import { FC, useState } from "react";

import { Button, Card } from "react-bootstrap";

import { FileText, Plus } from "lucide-react";

import { useGetCustomerInvoiceByServiceReportId } from "@/_services/hooks/invoice.hooks";
import Loader from "@/common/components/Loader";
import OrderEstimates from "@/modules/order/components/OrderInvoiceInfosPreview/components/OrderEstimates";
import OrderInvoices from "@/modules/order/components/OrderInvoiceInfosPreview/components/OrderInvoices";
import QuoteAssociation from "@/modules/order/components/OrderInvoiceInfosPreview/components/QuoteAssociation";

type Props = {
  serviceReportId: string;
  serviceData?: any;
};
const OrderInvoiceInfosPreview: FC<Props> = ({
  serviceReportId,
  serviceData,
}) => {
  const { data: customerInvoice, isLoading: isLoadingCustomerInvoice } =
    useGetCustomerInvoiceByServiceReportId(serviceReportId);
  const [showQuoteAssociation, setShowQuoteAssociation] = useState(false);
  const diagnosticCustomerEstimates =
    serviceData?.dwelling.agency?.diagnosticCustomer?.estimates || [];
  const customerEstimates =
    serviceData?.dwelling.agency?.customer?.estimates || [];
  const estimates = [...diagnosticCustomerEstimates, ...customerEstimates];
  const hasInvoiceFromServiceData =
    estimates.length > 0 &&
    estimates[0].related_invoices &&
    estimates[0].related_invoices.length > 0;

  // Determine if OrderInvoices should be shown
  const shouldShowOrderInvoices = customerInvoice || hasInvoiceFromServiceData;

  // Determine if OrderEstimates should be shown
  const shouldShowOrderEstimates = estimates.length > 0;

  const handleStartAssociation = () => {
    setShowQuoteAssociation(true);
  };

  return (
    <div className="max-w-6xl mx-auto min-h-screen">
      {isLoadingCustomerInvoice ? (
        <div className="mt-1 flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : !shouldShowOrderInvoices &&
        !shouldShowOrderEstimates &&
        !showQuoteAssociation ? (
        <>
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-sm bg-white">
              <Card.Body className="text-center py-16">
                <div className="flex flex-col items-center space-y-6">
                  <div className="p-4 bg-gray-100 rounded-full">
                    <FileText className="w-12 h-12 text-gray-400" />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Aucune facture / devis associée
                    </h3>
                    <p className="text-gray-600 max-w-md">
                      Vous devez d'abord l'associer à un devis pour pouvoir
                      générer une facture.
                    </p>
                  </div>

                  <div className="space-y-3 w-full max-w-sm">
                    <Button
                      size="lg"
                      onClick={handleStartAssociation}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Associer à un devis
                    </Button>

                    <p className="text-xs text-gray-500">
                      L'association à un devis est nécessaire pour générer une
                      facture
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </>
      ) : shouldShowOrderInvoices || shouldShowOrderEstimates ? (
        <>
          <div className="flex gap-6">
            {shouldShowOrderInvoices && (
              <div
                className={
                  shouldShowOrderEstimates ? "w-1/2" : "w-full max-w-sm mx-auto"
                }
              >
                <OrderInvoices
                  customerInvoice={customerInvoice?.data?.invoice}
                  estimateData={estimates}
                />
              </div>
            )}

            {shouldShowOrderEstimates && (
              <div
                className={
                  shouldShowOrderInvoices ? "w-1/2" : "w-full max-w-sm mx-auto"
                }
              >
                <div className="space-y-4">
                  {estimates.map((estimate: any) => (
                    <OrderEstimates
                      key={estimate.id}
                      estimate={estimate}
                      serviceId={serviceData?.id}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}

      {!shouldShowOrderInvoices &&
        !shouldShowOrderEstimates &&
        showQuoteAssociation && <QuoteAssociation serviceData={serviceData} />}
    </div>
  );
};

export default OrderInvoiceInfosPreview;
